import { useEffect, useState } from 'react'
import { Box, Typography, Divider } from '@mui/material'
import { Field, ErrorMessage } from 'formik'
import { format, parse, eachDayOfInterval } from 'date-fns'

import { useGetDisabledDate } from 'services/api/modules/leave/calendar'

import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import AdxDropzoneImagePdf from 'components/reusable/AdxDropzoneImagePdf'
import AdxInput from 'components/custom/AdxInput'
import AdxDateRangePicker from 'components/reusable/AdxDateRangePicker'
import getWeekValue from 'module/leave/util/getWeekValue'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'

const FillLeaveWithEntitlement = ({
  selectedLeave,
  leaveBalance,
  values,
  handleChange,
  handleBlur,
  setFieldValue,
  errors,
  setFieldTouched,
  touched,
  validateField,
  requestDateError,
  setDisableNextButton,
}) => {
  const { data: disabledCalendarData } = useGetDisabledDate(selectedLeave.leave_policy_id)

  const ph = disabledCalendarData?.data?.holiday?.public_holiday?.length
    ? disabledCalendarData?.data?.holiday?.public_holiday?.map((day) =>
      format(parse(day.date, `dd/MM/yyyy`, new Date()), `dd MMMM yyyy`)
    )
    : []

  const ch = disabledCalendarData?.data?.holiday?.custom_holiday?.length
    ? disabledCalendarData?.data?.holiday?.custom_holiday?.map((day) =>
      format(parse(day.date, `dd/MM/yyyy`, new Date()), `dd MMMM yyyy`)
    )
    : []

  const takenLeave = disabledCalendarData?.data?.leave?.length
    ? disabledCalendarData?.data?.leave?.map((day) =>
      format(parse(day.date, `yyyy-MM-dd`, new Date()), `dd MMMM yyyy`)
    )
    : []

  const quota = disabledCalendarData?.data?.full_quota?.length
    ? disabledCalendarData?.data?.full_quota?.map((day) =>
      format(parse(day.date, `yyyy-MM-dd`, new Date()), `dd MMMM yyyy`)
    )
    : []

  const offDay = disabledCalendarData?.data?.workday?.off_day?.map((day) => getWeekValue(day.day))

  const restDay = disabledCalendarData?.data?.workday?.rest_day?.map((day) => getWeekValue(day.day))

  const [balance, setBalance] = useState({})

  const calculateBalance = (entitlementCurrent, creditCurrent, carryForwardCurrent, duration) => {
    let entitlementBalance
    let creditBalance = creditCurrent
    let carryForwardBalance = carryForwardCurrent
    let durationRemaining = duration

    if (creditCurrent && durationRemaining > 0) {
      if (creditCurrent - durationRemaining <= 0) {
        creditBalance = 0
        durationRemaining = durationRemaining - creditCurrent
      } else {
        creditBalance = creditCurrent - durationRemaining
        durationRemaining = 0
      }
    }
    if (carryForwardCurrent && durationRemaining > 0) {
      if (carryForwardCurrent - durationRemaining <= 0) {
        carryForwardBalance = 0
        durationRemaining = durationRemaining - carryForwardCurrent
      } else {
        carryForwardBalance = carryForwardCurrent - durationRemaining
        durationRemaining = 0
      }
    }

    if (entitlementCurrent - durationRemaining <= 0) {
      entitlementBalance = 0
      durationRemaining = durationRemaining - entitlementCurrent
    } else {
      entitlementBalance = entitlementCurrent - durationRemaining
      durationRemaining = 0
    }

    return {
      creditBalance,
      carryForwardBalance,
      entitlementBalance,
    }
  }

  useEffect(() => {
    const bal = calculateBalance(
      selectedLeave?.entitlement?.balance,
      leaveBalance?.leave_credit[0]?.applicable_leave?.find(
        (uItem) => uItem.leave_policy_id === selectedLeave.leave_policy_id
      )
        ? leaveBalance?.leave_credit[0]?.leave_credit.balance
        : null,
      selectedLeave?.carry_forward?.balance ? selectedLeave?.carry_forward?.balance : null,
      values.duration
    )

    setBalance(bal)
  }, [values.duration, selectedLeave]) // eslint-disable-line react-hooks/exhaustive-deps

  // date conflicts comparison function
  const selectedDates =
    values.startDate && values.endDate
      ? eachDayOfInterval({
        start: parse(values.startDate, 'dd/MM/yyyy', new Date()),
        end: parse(values.endDate, 'dd/MM/yyyy', new Date()),
      }).map((date) => format(date, 'dd MMMM yyyy'))
      : []

  const getConflictingDates = (selectedDates, takenLeave) => {
    return selectedDates.filter((date) => takenLeave.includes(date))
  }

  const hasDateConflict = selectedDates.some((date) => takenLeave.includes(date))
  const conflictingDates = getConflictingDates(selectedDates, takenLeave)

  useEffect(() => {
    if (hasDateConflict === true) setDisableNextButton(true)
    else setDisableNextButton(false)
  }, [hasDateConflict])

  const leavePolicy = selectedLeave?.leave_policy

  return (
    <>
      {/* Shift and select date form */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', tablet: 'row' },

            gap: 2,
          }}
        >
          <Field
            as={AdxDropdown}
            name="shiftLeave"
            disabled={selectedLeave.leave_policy.half_day_option ? false : true}
            value={values.shiftLeave}
            onChange={handleChange}
            onBlur={handleBlur}
            label={'Shift'}
            sx={{ width: { xs: '100%', tablet: '40%' } }}
          >
            <AdxMenuItem selectedvalue={values.shiftLeave} value={'Full Day'}>
              Full day
            </AdxMenuItem>
            <AdxMenuItem selectedvalue={values.shiftLeave} value={'AM'}>
              Half day (AM)
            </AdxMenuItem>
            <AdxMenuItem selectedvalue={values.shiftLeave} value={'PM'}>
              Half day (PM)
            </AdxMenuItem>
          </Field>

          <Field
            as={AdxDateRangePicker}
            disabledDates={
              leavePolicy.include_non_working_days === 0 && disabledCalendarData
                ? [...ph, ...ch, ...takenLeave, ...quota]
                : []
            }
            disableCertainDays={
              leavePolicy.include_non_working_days === 0 && disabledCalendarData
                ? [...offDay, ...restDay]
                : []
            }
            minDayFromToday={selectedLeave.leave_policy.day_prior}
            disablePast={selectedLeave.leave_policy.day_prior}
            restrictions={
              values.shiftLeave === 'AM' || values.shiftLeave === 'PM'
                ? 1
                : selectedLeave.leave_policy.restriction_amount
            }
            startCycle={new Date(selectedLeave.cycle_start_date)}
            endCycle={new Date(selectedLeave.cycle_end_date)}
            values={{
              startDate: values.startDate,
              endDate: values.endDate,
              duration: values.duration,
            }}
            setValue={(e) => {
              setFieldValue('startDate', e.startDate)
              setFieldValue('endDate', e.endDate)
              setFieldValue('duration', e.duration)
            }}
            label={'Date'}
            name={'startDate'}
            sx={{ width: '60%' }}
            errorMessage={
              requestDateError ? (
                <AdxErrorMessage>{requestDateError}</AdxErrorMessage>
              ) : (
                <ErrorMessage name="startDate" component={AdxErrorMessage} />
              )
            }
            isErrorShown={requestDateError ? true : errors?.startDate && touched?.startDate}
            setFieldTouched={setFieldTouched}
            validateField={validateField}
          />
        </Box>

        {hasDateConflict && (
          <Box
            sx={{
              bgcolor: 'error.light',
              border: 1,
              borderRadius: '4.5px',
              borderColor: 'error.main',
              p: 3,
            }}
          >
            <Typography>
              Your selected date range appears to be conflicting with other leave request.
            </Typography>
            <ul>
              {conflictingDates?.map((date, index) => (
                <li>
                  <Typography>{date}</Typography>
                </li>
              ))}
            </ul>
            <Typography>
              Make sure to withdraw any other affected leave application coincide with the dates
              before applying new leave request.
            </Typography>
          </Box>
        )}

        {/* Entitlement Restriction box */}
        {leavePolicy?.day_prior || leavePolicy?.quota_amount || leavePolicy?.restriction_amount ? (
          <Box
            sx={{
              backgroundColor: 'gray-background.main',
              borderRadius: 1.25,
              p: 2.5,
              display: 'flex',
              flexDirection: 'column',
              rowGap: { xs: 1.5, sm: 1 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="body1" color="error.main">
                Restrictions
              </Typography>
            </Box>
            {leavePolicy?.quota_amount && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: { xs: null, sm: 'space-between' },
                }}
              >
                <Typography variant="body1" color="light-gray-text.main">
                  Maximum Number of Employees Quota
                </Typography>

                <Typography variant="body1" color="dark-text.main">
                  {leavePolicy?.quota_amount}{' '}
                  {leavePolicy?.quota_unit === 'percent'
                    ? leavePolicy?.quota_unit
                    : leavePolicy?.quota_amount > 1
                      ? 'persons'
                      : 'person'}{' '}
                  {leavePolicy?.quota_category === 'company' ? 'of the' : 'per'}{' '}
                  {leavePolicy?.quota_category}
                </Typography>
              </Box>
            )}
            {leavePolicy?.restriction_amount && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: { xs: null, sm: 'space-between' },
                }}
              >
                <Typography variant="body1" color="light-gray-text.main">
                  Number of Consecutive Days Allowed
                </Typography>

                <Typography variant="body1" color="dark-text.main">
                  {leavePolicy?.restriction_amount} days in a row
                </Typography>
              </Box>
            )}

            {leavePolicy?.day_prior && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: { xs: null, sm: 'space-between' },
                }}
              >
                <Typography variant="body1" color="light-gray-text.main">
                  Number of Days Prior to Application
                </Typography>

                <Typography variant="body1" color="dark-text.main">
                  {leavePolicy?.day_prior} {leavePolicy?.day_prior > 1 ? 'days' : 'day'}
                </Typography>
              </Box>
            )}
          </Box>
        ) : null}

        {/* Entitlement details box */}
        <Box
          sx={{
            backgroundColor: 'gray-background.main',
            borderRadius: 1.25,
            p: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: { xs: null, sm: 'space-between' },
            }}
          >
            <Typography variant="body1" color="light-gray-text.main">
              Available entitlement
            </Typography>
            <Typography variant="body1" color="dark-text.main">
              {selectedLeave.entitlement.balance === 1
                ? `${selectedLeave.entitlement.balance} day`
                : `${selectedLeave.entitlement.balance} days`}
            </Typography>
          </Box>
          {leaveBalance?.leave_credit[0]?.applicable_leave?.find(
            (uItem) => uItem.leave_policy_id === selectedLeave.leave_policy_id
          ) && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: { xs: null, sm: 'space-between' },
                }}
              >
                <Typography variant="body1" color="light-gray-text.main">
                  Available replacement credit
                </Typography>
                <Typography variant="body1" color="dark-text.main">
                  {leaveBalance?.leave_credit[0]?.leave_credit.balance
                    ? leaveBalance?.leave_credit[0]?.leave_credit.balance === 1
                      ? `${leaveBalance?.leave_credit[0]?.leave_credit.balance} day`
                      : `${leaveBalance.leave_credit[0]?.leave_credit.balance} days`
                    : '0 day'}
                </Typography>
              </Box>
            )}
          {selectedLeave.carry_forward && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: { xs: null, sm: 'space-between' },
              }}
            >
              <Typography variant="body1" color="light-gray-text.main">
                Available carry forward
              </Typography>
              <Typography variant="body1" color="dark-text.main">
                {selectedLeave.carry_forward.balance
                  ? selectedLeave.carry_forward.balance === 1
                    ? `${selectedLeave.carry_forward.balance} day`
                    : `${selectedLeave.carry_forward.balance} days`
                  : '0 day'}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: { xs: null, sm: 'space-between' },
            }}
          >
            <Typography variant="body1" color="light-gray-text.main">
              Days to be requested
            </Typography>
            <Typography variant="body1" color="dark-text.main">
              {values.duration} {values.duration > 1 ? 'days' : 'day'}
            </Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: { xs: null, sm: 'space-between' },
            }}
          >
            <Typography variant="body1" color="light-gray-text.main">
              Entitlement balance upon approval
            </Typography>
            <Typography variant="body1" color="dark-text.main">
              {balance.entitlementBalance} {balance.entitlementBalance > 1 ? 'days' : 'day'}
            </Typography>
          </Box>
          {leaveBalance?.leave_credit[0]?.applicable_leave?.find(
            (uItem) => uItem.leave_policy_id === selectedLeave.leave_policy_id
          ) && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: { xs: null, sm: 'space-between' },
                }}
              >
                <Typography variant="body1" color="light-gray-text.main">
                  Replacement credit balance upon approval
                </Typography>
                <Typography variant="body1" color="dark-text.main">
                  {balance.creditBalance} {balance.creditBalance > 1 ? 'days' : 'day'}
                </Typography>
              </Box>
            )}
          {selectedLeave.carry_forward && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: { xs: null, sm: 'space-between' },
              }}
            >
              <Typography variant="body1" color="light-gray-text.main">
                Carry forward balance upon approval
              </Typography>
              <Typography variant="body1" color="dark-text.main">
                {balance.carryForwardBalance} {balance.carryForwardBalance > 1 ? 'days' : 'day'}
              </Typography>
            </Box>
          )}
          {errors?.duration && (
            <Typography variant="caption" color="error.main">
              {errors?.duration}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Multine input for reasons / remarks */}
      <Field
        as={AdxInput}
        label={`Leave purpose ${selectedLeave.leave_policy.reason_required ? '(Required)' : '(Optional)'
          } `}
        multiline
        minRows={4}
        name="reason"
        value={values.reason}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="Enter reason here..."
        charLimit={120}
        sxInput={{ p: 2, borderRadius: '10px' }}
        errorMessage={<ErrorMessage name="reason" component={AdxErrorMessage} />}
        isErrorShown={errors.reason && touched.reason}
      />

      {/* Dropzone to upload documents */}
      <Field name="file" values={values.file} type="hidden" />

      <AdxDropzoneImagePdf
        file={
          Object.keys(values.file).length !== 0
            ? { name: values?.file?.name, url: values?.file?.preview || values?.file?.awsUrl }
            : null
        }
        label={`Attachment
            ${selectedLeave.leave_policy.attachment_required ? ' (Required)' : ' (Optional)'}`}
        title="Drag an attachment from computer"
        subtitle="or click here to upload"
        folder="leave_attachment"
        setFieldValue={setFieldValue}
        errorMessage={<ErrorMessage name="file" component={AdxErrorMessage} />}
        isErrorShown={errors.file && touched.file}
      />
    </>
  )
}

export default FillLeaveWithEntitlement
